import React, { useEffect, useState, Suspense, useMemo } from "react";
import OrganizationBase from "./OrganizationBase";
import SideBar from "../../components/sidebar/Sidebar";
import { getUser, getTenantDetails ,getOrganisationNetworth ,getTenantLiabilitiesValue,getTenantNetworthForGraph , } from "../../services/user-service";
import { getRealEstates } from "../../services/RealEstate-service";
import { getVehicle } from "../../services/VehicleService";
import { getInternationalAsset } from "../../services/InternationalAssetService";
import { Notification } from "../../components/alerting-visitors/notification";
import "../../pages/user-route/UserDashboard.css";
import JointAccount from "../../pages/JointAccount";
import MultiOrgUserInvite from "./MultiOrgUserInvite";
import DirectOrgUser from "../../pages/DirectOrgUser";

import AOS from "aos";
import "aos/dist/aos.css";
import Userdashboard from "../user-route/Userdashboard";
import { Switch, FormControlLabel } from "@mui/material";
import { motion } from "framer-motion";
import {
  differenceInMonths,
  differenceInYears,
  format,
  parseISO,
} from "date-fns";
import {
  Area,
  AreaChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Sector,
  Brush,
} from "recharts";
import { formatAmount } from "../../components/Common";
import { useNavigate } from "react-router-dom";

export default function OrganizationDashboard() {
  const navigate = useNavigate();
  const [user] = useState(getUser());
  const [tenant, setTenant] = useState(null);
  const [greeting, setGreeting] = useState("Good Morning");
  const [currDateTime, setCurrDateTime] = useState({ date: "", time: "" });
  const [showValues, setShowValues] = useState(true);
  const [addJointPopup, setAddJointPopup] = useState(false);
  const [addOrgUserPopup, setAddOrgUserPopup] = useState(false);
  const [inviteMode, setInviteMode] = useState(null); // "single" | "multiple"
  const [networthData, setNetworthData] = useState(null);
  const [liabilitiesValue, setLiabilitiesValue] = useState(null);
  const [realEstateMortgage, setRealEstateMortgage] = useState(0);
  const [vehicleMortgage, setVehicleMortgage] = useState(0);
  const [internationalMortgage, setInternationalMortgage] = useState(0);
  const [networthGraph, setNetworthGraph] = useState(null);
  const [timeFilter, setTimeFilter] = useState("1month");
  const [activeAssetIndex, setActiveAssetIndex] = useState(null);
  const [activeLiabilityIndex, setActiveLiabilityIndex] = useState(null);
  const [selectPieChart, setSelectPieChart] = useState(false);
  const [selectAreaChart, setSelectAreaChart] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showDirectOrgUser, setShowDirectOrgUser] = useState(false);

  const toggleJointAccount = () => setAddJointPopup(!addJointPopup);

  useEffect(() => {
    AOS.init();
  }, []);

  useEffect(() => {
    if (user?.id) {
      getTenantDetails(user.id)
        .then((res) => setTenant(res))
        .catch(() => {});
    }
  }, [user?.id]);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const hours = now.getHours();
      setGreeting(
        hours < 12
          ? "Good Morning"
          : hours < 18
          ? "Good Afternoon"
          : "Good Evening"
      );
      setCurrDateTime({
        date: now
          .toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
          .replace(/ /g, "-"),
        time: now.toLocaleTimeString(),
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userId = user?.commonId;
        const token = `Bearer ${localStorage.getItem('token')}`;

        const networth = await getOrganisationNetworth(userId);
        setNetworthData(networth);

        const realEstateData = await getRealEstates(token, userId);
        const realEstateMortgageTotal = realEstateData.reduce(
          (sum, item) => sum + parseFloat(item.realEstate?.mortgage || 0),
          0
        );
        setRealEstateMortgage(realEstateMortgageTotal);
        const vehicleData = await getVehicle(token, userId);
        const vehicleMortgageTotal = vehicleData.reduce(
          (sum, item) => sum + parseFloat(item.mortgage?.mortgage || 0),
          0
        );
        setVehicleMortgage(vehicleMortgageTotal);
        const internationalAssets = await getInternationalAsset(token, userId);
        const internationalMortgageTotal = internationalAssets.reduce((sum, item) => {
          const mortgageAmount =
            parseFloat(item.assetData?.mortgage?.mortgage || 0) ||
            parseFloat(item.assetData?.mortgages?.[0]?.mortgage || 0);
          return sum + mortgageAmount;
        }, 0);
        setInternationalMortgage(internationalMortgageTotal);
        const liabilities = await getTenantLiabilitiesValue(userId);
        setLiabilitiesValue(liabilities);
        const graphData = await getTenantNetworthForGraph(userId);
        setNetworthGraph(graphData);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.commonId) {
      fetchData();
    }
  }, [user?.commonId]);


  const assetData = [
    { name: "Real Estate", value: networthData?.RealEstate },
    { name: "Banks", value: networthData?.Bank },
    { name: "Investment", value: networthData?.Investment },
    { name: "Crypto", value: networthData?.Crypto },
    { name: "Jewelry", value: networthData?.Jewelry },
    { name: "Insurance", value: networthData?.Insurance },
    { name: "Vehicle", value: networthData?.Vehicles },
    { name: "Business-Passive Income", value: networthData?.Income },
    { name: "Miscellaneous Asset", value: networthData?.OtherAsset },
    { name: "International Asset", value: networthData?.InternationalAsset },
  ];

  const liabilitiesData = [
    { name: "Credit Card", value: liabilitiesValue?.CreditCard },
    { name: "Mortgage/Loan", value: liabilitiesValue?.Mortgage },
    { name: "Personal Loan", value: liabilitiesValue?.PersonalLoan },
  ];

  const allAssetsValuesZero = assetData.every((item) => item.value === 0);
  const allLiabilitiesValuesZero = liabilitiesData.every((item) => item.value === 0);

  const COLORS = [
    "#6A89CC", "#78E08F", "#F8C291", "#E77F67", "#82CCDD",
    "#60A3BC", "#E55039", "#F6B93B", "#B8E994", "#546DE5",
  ];

  const LIGHT_COLORS = ["#90ee90", "#90c6ee", "#ee9090"];

  const toggleSelectChart = (value) => {
    if (value === "pie") {
      setSelectPieChart(true);
      setSelectAreaChart(false);
    } else {
      setSelectPieChart(false);
      setSelectAreaChart(true);
    }
  };


  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, value } = props;

    return (
      <g>
        <motion.g
          initial={{ scale: 1 }}
          animate={{ scale: 1.08 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          style={{ transformOrigin: `${cx}px ${cy}px` }}
        >
          <Sector
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={startAngle}
            endAngle={endAngle}
            fill={fill}
          />
        </motion.g>
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          fill="#333"
          style={{ fontWeight: 500, fontSize: '12px' }}
        >
          {payload.name}
        </text>
        <text
          x={cx}
          y={cy + 8}
          textAnchor="middle"
          fill={fill}
          style={{ fontSize: '16px', fontWeight: 600 }}
        >
          ${value.toLocaleString()}
        </text>
      </g>
    );
  };

  const getRoundedEvenValue = (value, roundTo) => {
    const roundedValue = Math.ceil(value / roundTo);
    return roundedValue % 2 === 0
      ? roundedValue * roundTo
      : roundedValue * roundTo + roundTo;
  };

  const filterDataByTimeRange = (data, range) => {
    const now = new Date();
    let startDate;

    switch (range) {
      case "1month":
        startDate = new Date();
        startDate.setMonth(now.getMonth() - 1);
        break;
      case "6months":
        startDate = new Date();
        startDate.setMonth(now.getMonth() - 6);
        break;
      case "1year":
        startDate = new Date();
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case "5years":
        startDate = new Date();
        startDate.setFullYear(now.getFullYear() - 5);
        break;
      case "all-time":
        return data;
      default:
        return data;
    }

    const filteredData = data.filter(
      (item) => new Date(item.date) >= startDate
    );

    const dataMap = filteredData.reduce((acc, item) => {
      const formattedDate = new Date(item.date).toISOString().split("T")[0];
      acc[formattedDate] = item;
      return acc;
    }, {});

    const dateRangeData = [];
    let latestNetWorth = 0;
    startDate.setDate(startDate.getDate() + 1);

    for (
      let date = new Date(startDate);
      date <= now;
      date.setDate(date.getDate() + 1)
    ) {
      const formattedDate = date.toISOString().split("T")[0];
      if (dataMap[formattedDate]) {
        latestNetWorth = dataMap[formattedDate].netWorthValue;
        dateRangeData.push(dataMap[formattedDate]);
      } else {
        dateRangeData.push({
          date: formattedDate,
          netWorthValue: latestNetWorth,
        });
      }
    }

    return dateRangeData;
  };

  const handleTimeFilterChange = (range) => {
    setTimeFilter(range);
  };

  const filteredData = useMemo(() =>
    networthGraph ? filterDataByTimeRange(networthGraph, timeFilter) : [],
    [networthGraph, timeFilter]
  );

  const { roundedMaxValue, roundedMinValue } = useMemo(() => {
    let max = 0;
    let min = 0;

    if (filteredData.length > 0) {
      max = filteredData.reduce((maxVal, item) =>
        Math.max(maxVal, item.netWorthValue || 0),
        0
      );
      min = filteredData.reduce((minVal, item) =>
        Math.min(minVal, item.netWorthValue || 0),
        0
      );
    }

    const roundedMax = max > 0 ? getRoundedEvenValue(max, 1000) : 1000;
    const roundedMinRaw = min < 0 ? getRoundedEvenValue(Math.abs(min), 1000) : 0;

    return {
      roundedMaxValue: roundedMax,
      roundedMinValue: -roundedMinRaw
    };
  }, [filteredData]);

  const [brushIndexRange, setBrushIndexRange] = useState({ startIndex: 0, endIndex: 0 });

  useEffect(() => {
    if (filteredData.length > 0) {
      setBrushIndexRange({ startIndex: 0, endIndex: filteredData.length - 1 });
    } else {
      setBrushIndexRange({ startIndex: 0, endIndex: 0 });
    }
  }, [filteredData.length]);

  const onBrushChange = (range) => {
    if (range) {
      setBrushIndexRange(range);
    }
  };

  const tickFormatter = (dateString, filter) => {
    const date = parseISO(dateString);
    const now = new Date();
    const diffMonths = differenceInMonths(now, date);
    const diffYears = differenceInYears(now, date);

    switch (filter) {
      case "1month":
        if (date.getDate() % 7 === 0) {
          return format(date, "MMM d");
        }
        return "";
      case "6months":
        if (diffMonths <= 6) {
          return format(date, "MMM yyyy");
        }
        return "";
      case "1year":
        if (diffYears <= 1) {
          return format(date, "MMM yyyy");
        }
        return "";
      case "5years":
      case "all-time":
        if (diffYears <= 5) {
          return format(date, "yyyy");
        }
        return "";
      default:
        return "";
    }
  };

  const formatTooltip = (value) => formatAmount(value);

  if (loading) return <p>Loading...</p>;

  return (
    <>
      {user?.role === "ORG_USER" ? (
        <Userdashboard />
      ) : (
        <OrganizationBase>
          <div style={{ marginTop: "4rem" }} className="otherElement">
            <SideBar>
              <div style={{ backgroundColor: "yellow", padding: "3px 0 6px 0" }}>
                <Suspense fallback={<div>Loading...</div>}>
                  <Notification />
                </Suspense>
              </div>

              <div className="Dashboard_main">
                <div className="dashboard_left_org">
                  <div className="user_details_dash">
                    <div className="UserDetails">
                      <p className="Username_details" style={{ margin: 0 }}>
                        {greeting},{" "}
{/*                         {tenant?.organizationName */}
                          {(user.role === "TENANT_ADMIN" && tenant?.organizationName)
                          ? tenant.organizationName
                          : user?.firstName
                          ? user.firstName
                          : user?.username || "User"}
                        <br />
                        Welcome to I
                        <span style={{ color: "#059212" }}>-</span>Chest.com
                      </p>
                      <p className="date_time_details">
                        Today {currDateTime.date} {currDateTime.time}
                      </p>
                    </div>
                    <div className="dashboard_right">
                                      <div
                                        style={{ display: "flex", gap: "15px", marginBottom: "20px" }}
                                      >
                                        <div className="dashboard_action_btn" onClick={toggleJointAccount}>
                                          Invite Secondary Account
                                        </div>

                                        <div
                                          className="dashboard_action_btn"
                                          onClick={() => setAddOrgUserPopup(true)}
                                        >
                                          Invite User Account
                                        </div>
                                       {user?.role === "TENANT_ADMIN" && (
                                         <div
                                           className="dashboard_action_btn"
                                           onClick={() => setShowDirectOrgUser(true)}
                                         >
                                           Create Org User
                                         </div>
                                       )}
                                      </div>
                                    </div>
                  </div>

                  <div className="user_chart_dash_main">
                    <div className="chart_button">
                      <button
                        className={`chart_all_button ${selectAreaChart ? "selected" : ""}`}
                        onClick={() => toggleSelectChart("area")}
                      >
                        Total Net Worth
                      </button>
                      <button
                        className={`chart_all_button ${selectPieChart ? "selected" : ""}`}
                        onClick={() => toggleSelectChart("pie")}
                      >
                        Assets/Liabilities Value
                      </button>
                    </div>

                    <div className="user_charts">
                      {selectAreaChart ? (
                        <div className="user_chart_dash">
                          <div className="chat_filter_buttons">
                            <div className="Totalnetworth">
                              <p>
                                Approx Net Worth:{" "}
                                <span className="networth_amount">
                                  {formatAmount(networthData?.AdjustedNetWorth)}
                                </span>
                              </p>
                            </div>
                            <div>
                              <button
                                className="filter_button_style"
                                onClick={() => handleTimeFilterChange("1month")}
                                style={{ color: timeFilter === "1month" ? "blue" : "black" }}
                              >
                                1 Month
                              </button>
                              <button
                                className="filter_button_style"
                                onClick={() => handleTimeFilterChange("6months")}
                                style={{ color: timeFilter === "6months" ? "blue" : "black" }}
                              >
                                6 Months
                              </button>
                              <button
                                className="filter_button_style"
                                onClick={() => handleTimeFilterChange("1year")}
                                style={{ color: timeFilter === "1year" ? "blue" : "black" }}
                              >
                                1 Year
                              </button>
                              <button
                                className="filter_button_style"
                                onClick={() => handleTimeFilterChange("5years")}
                                style={{ color: timeFilter === "5years" ? "blue" : "black" }}
                              >
                                5 Years
                              </button>
                              <button
                                className="filter_button_style"
                                onClick={() => handleTimeFilterChange("all-time")}
                                style={{ color: timeFilter === "all-time" ? "blue" : "black" }}
                              >
                                All Time
                              </button>
                            </div>
                          </div>

                          <div className="user_areachart" style={{ width: "100%", height: 350 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart
                                data={filteredData}
                                margin={{ top: 10, right: 30, left: 30, bottom: 0 }}
                              >
                                <defs>
                                  <linearGradient id="colorNetworth" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#7286D3" stopOpacity={0.8} />
                                    <stop offset="95%" stopColor="#7286D3" stopOpacity={0} />
                                  </linearGradient>
                                </defs>

                                <XAxis
                                  dataKey="date"
                                  type="category"
                                  axisLine={false}
                                  tickLine={false}
                                  interval="preserveStartEnd"
                                  minTickGap={48}
                                  tickFormatter={(tick) => tickFormatter(tick, timeFilter)}
                                  tick={{ fontSize: 14, fill: "#2C74B3" }}
                                  height={36}
                                />

                                <YAxis
                                  domain={[roundedMinValue, roundedMaxValue]}
                                  axisLine={false}
                                  tickLine={false}
                                  tickCount={6}
                                  tick={{ fontSize: 14, fill: "#2C74B3" }}
                                  ticks={[
                                    roundedMinValue,
                                    0,
                                    roundedMaxValue / 4,
                                    roundedMaxValue / 2,
                                    (roundedMaxValue * 3) / 4,
                                    roundedMaxValue,
                                  ]}
                                  tickFormatter={formatAmount}
                                  width={80}
                                />

                                <Tooltip
                                  contentStyle={{
                                    borderRadius: 8,
                                    border: "none",
                                    boxShadow: "0 6px 24px #aaa",
                                    fontSize: 15,
                                  }}
                                  labelStyle={{ fontWeight: "bold", color: "#404040" }}
                                  cursor={{ fill: "rgba(44,116,179,0.08)" }}
                                  formatter={(value) => [`Networth: ${formatAmount(value)}`]}
                                  labelFormatter={(label) =>
                                    format(parseISO(label), "eeee, d MMM, yyyy")
                                  }
                                />

                                <Area
                                  type="monotone"
                                  dataKey="netWorthValue"
                                  stroke="#2C74B3"
                                  strokeWidth={2}
                                  fillOpacity={1}
                                  fill="url(#colorNetworth)"
                                  baseLine={0}
                                  dot={false}
                                  activeDot={{ r: 5, stroke: "#2C74B3", fill: "#fff", strokeWidth: 2 }}
                                />

                                <Brush
                                  dataKey="date"
                                  height={28}
                                  stroke="#2C74B3"
                                  travellerWidth={5}
                                  tickFormatter={(tick) => tickFormatter(tick, timeFilter)}
                                  startIndex={brushIndexRange.startIndex}
                                  endIndex={brushIndexRange.endIndex}
                                  onChange={onBrushChange}
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      ) : (
                        <div className="user_pie_chart_main" style={{ display: "flex", gap: "40px", flexWrap: "wrap" }}>
                          <motion.div
                            initial={{ opacity: 0, x: -50, scale: 0.95 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className="user_pie_chart_with_assets"
                            style={{ flex: 1, minWidth: "300px", display: "flex", gap: "20px", alignItems: "center" }}
                          >
                            <div className="user_pie_chart" style={{ height: 300, position: "relative" }}>
                              {allAssetsValuesZero ? (
                                <p style={{ fontWeight: "500" }}>No data to show</p>
                              ) : (
                                <>
                                  <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                      <Pie
                                        data={assetData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        innerRadius={60}
                                        outerRadius={120}
                                        dataKey="value"
                                        paddingAngle={2}
                                        activeIndex={activeAssetIndex}
                                        activeShape={renderActiveShape}
                                        onMouseEnter={(_, index) => setActiveAssetIndex(index)}
                                        onMouseLeave={() => setActiveAssetIndex(null)}
                                        animationBegin={0}
                                        animationDuration={800}
                                        animationEasing="ease-in-out"
                                        startAngle={90}
                                        endAngle={450}
                                      >
                                        {assetData.map((entry, index) => (
                                          <Cell
                                            key={`cell-asset-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                            style={{
                                              outline: 'none',
                                              cursor: 'pointer',
                                              transform: activeAssetIndex === index ? 'scale(1.125)' : 'scale(1)',
                                              transformOrigin: 'center',
                                              transition: 'transform 0.8s ease-in-out, filter 0.8s ease-in-out',
                                              filter: activeAssetIndex === index ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'none',
                                            }}
                                          />
                                        ))}
                                      </Pie>
                                      <Tooltip
                                        formatter={formatTooltip}
                                        contentStyle={{
                                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                          border: '1px solid #ccc',
                                          borderRadius: '4px',
                                          padding: '8px'
                                        }}
                                      />
                                    </PieChart>
                                  </ResponsiveContainer>
                                  <p style={{ fontWeight: "500", textAlign: "center", marginTop: 12 }}>
                                    All Assets Value
                                  </p>
                                </>
                              )}
                            </div>
                            <div className="user_pie_chart_details" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                              {assetData.map(
                                (data, index) =>
                                  data.value > 0 && (
                                    <div
                                      className="pie_chart_details"
                                      key={`asset-detail-${index}`}
                                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                                    >
                                      <div
                                        className="pie_chart_detail_box"
                                        style={{
                                          width: 16,
                                          height: 16,
                                          backgroundColor: COLORS[index],
                                          borderRadius: '50%',
                                        }}
                                      ></div>
                                      <p style={{ margin: 0 }}>{data.name}</p>
                                    </div>
                                  )
                              )}
                            </div>
                          </motion.div>

                          <motion.div
                            initial={{ opacity: 0, x: 50, scale: 0.95 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                            className="user_pie_chart_with_liabilities"
                            style={{ flex: 1, minWidth: "300px", display: "flex", gap: "20px", alignItems: "center" }}
                          >
                            <div className="user_pie_chart" style={{ height: 300, position: "relative" }}>
                              {allLiabilitiesValuesZero ? (
                                <p style={{ fontWeight: "500" }}>No data to show</p>
                              ) : (
                                <>
                                  <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                      <Pie
                                        data={liabilitiesData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        innerRadius={60}
                                        outerRadius={120}
                                        dataKey="value"
                                        paddingAngle={2}
                                        activeIndex={activeLiabilityIndex}
                                        activeShape={renderActiveShape}
                                        onMouseEnter={(_, index) => setActiveLiabilityIndex(index)}
                                        onMouseLeave={() => setActiveLiabilityIndex(null)}
                                        animationBegin={0}
                                        animationDuration={800}
                                        animationEasing="ease-in-out"
                                        startAngle={90}
                                        endAngle={450}
                                      >
                                        {liabilitiesData.map((entry, index) => (
                                          <Cell
                                            key={`cell-liability-${index}`}
                                            fill={LIGHT_COLORS[index % LIGHT_COLORS.length]}
                                            style={{
                                              outline: 'none',
                                              cursor: 'pointer',
                                              transform: activeLiabilityIndex === index ? 'scale(1.125)' : 'scale(1)',
                                              transformOrigin: 'center',
                                              transition: 'transform 0.8s ease-in-out, filter 0.8s ease-in-out',
                                              filter: activeLiabilityIndex === index ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'none',
                                            }}
                                          />
                                        ))}
                                      </Pie>
                                      <Tooltip
                                        formatter={formatTooltip}
                                        contentStyle={{
                                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                          border: '1px solid #ccc',
                                          borderRadius: '4px',
                                          padding: '8px'
                                        }}
                                      />
                                    </PieChart>
                                  </ResponsiveContainer>
                                  <p style={{ fontWeight: "500", textAlign: "center", marginTop: 12 }}>
                                    All Liabilities Value
                                  </p>
                                </>
                              )}
                            </div>
                            <div className="user_pie_chart_details" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                              {liabilitiesData.map(
                                (data, index) =>
                                  data.value > 0 && (
                                    <div
                                      className="pie_chart_details"
                                      key={`liability-detail-${index}`}
                                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                                    >
                                      <div
                                        className="pie_chart_detail_box"
                                        style={{
                                          width: 16,
                                          height: 16,
                                          backgroundColor: LIGHT_COLORS[index],
                                          borderRadius: '50%',
                                        }}
                                      ></div>
                                      <p style={{ margin: 0 }}>{data.name}</p>
                                    </div>
                                  )
                              )}
                            </div>
                          </motion.div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {addJointPopup && (
                <div
                  className="beneficiary-popup"
                  data-aos="fade-up"
                  data-aos-offset="200"
                  data-aos-delay="50"
                  data-aos-duration="800"
                >
                  <div className="beneficiary-content">
                    <Suspense fallback={<div>Loading...</div>}>
                      <JointAccount
                        mode="secondary"
                        userId={user?.commonId}
                        onBack={() => setAddJointPopup(false)}
                      />
                    </Suspense>
                  </div>
                </div>
              )}
              {showDirectOrgUser && (
                <div
                  className="beneficiary-popup"
                  data-aos="fade-up"
                  data-aos-offset="200"
                  data-aos-delay="50"
                  data-aos-duration="800"
                >
                  <div className="beneficiary-content">
                    <Suspense fallback={<div>Loading...</div>}>
                      <DirectOrgUser
                        onBack={() => setShowDirectOrgUser(false)}
                      />
                    </Suspense>
                  </div>
                </div>
              )}

{/*               {addOrgUserPopup && ( */}
{/*                 <div */}
{/*                   className="beneficiary-popup" */}
{/*                   data-aos="fade-up" */}
{/*                   data-aos-offset="200" */}
{/*                   data-aos-delay="50" */}
{/*                   data-aos-duration="800" */}
{/*                 > */}
{/*                   <div className="beneficiary-content"> */}
{/*                     <Suspense fallback={<div>Loading...</div>}> */}
{/*                       <JointAccount */}
{/*                         mode="orgUser" */}
{/*                         userId={user?.commonId} */}
{/*                         onBack={() => setAddOrgUserPopup(false)} */}
{/*                       /> */}
{/*                     </Suspense> */}
{/*                   </div> */}
{/*                 </div> */}
{/*               )} */}
                 {addOrgUserPopup && (
                   <div className="beneficiary-popup">
                     <div className="beneficiary-content">

                      {!inviteMode && (
                        <button
                          className="invite-close-btn"
                          onClick={() => setAddOrgUserPopup(false)}
                          aria-label="Close"
                        />
                      )}


                       {/* STEP 1: SELECT MODE */}
                       {!inviteMode && (
                         <div style={{ textAlign: "center" }}>
                           <h2>Invite Organization User</h2>

                           <button
                             className="dashboard_action_btn"
                             onClick={() => setInviteMode("single")}
                           >
                             Invite Single User
                           </button>

                           <button
                             className="dashboard_action_btn"
                             onClick={() => setInviteMode("multiple")}
                             style={{ marginLeft: "10px" }}
                           >
                             Invite Multiple Users (CSV)
                           </button>
                         </div>
                       )}

                       {/* STEP 2: SINGLE USER (EXISTING FLOW) */}
                       {inviteMode === "single" && (
                         <JointAccount
                           mode="orgUser"
                           userId={user?.commonId}
                           onBack={() => {
                             setInviteMode(null);
                             setAddOrgUserPopup(false);
                           }}
                         />
                       )}

                       {/* STEP 3: MULTIPLE USERS */}
                       {inviteMode === "multiple" && (
                         <MultiOrgUserInvite
                           userId={user?.commonId}
                           onBack={() => {
                             setInviteMode(null);
                             setAddOrgUserPopup(false);
                           }}
                         />
                       )}

                     </div>
                   </div>
                 )}

            </SideBar>
          </div>
        </OrganizationBase>
      )}
    </>
  );
}